import React, { useState, useMemo, useCallback } from 'react';
import Table from '../../components/Table/Table';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import EmployeeForm from '../../components/HR/EmployeeForm';
import { PageHeader, FilterBar, ErrorState } from '../../components/Common';
import type { FilterConfig } from '../../components/Common';
import { Employee, TableColumn } from '../../types';
import { useBranches } from '../../hooks/useBranches';
import { useRanks } from '../../hooks/useRanks';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { getArabicRankName } from '../../utils/rankUtils';
import { bumpCodePrefixSequence } from '../../utils/codePrefixUtils';
import {
  useEmployees,
  useCreateEmployee,
  useUpdateEmployee,
  useDeleteEmployee,
} from '../../hooks/useEmployees';

const Employees: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | undefined>();
  const [userRole] = useState('admin');

  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 100 },
  }), []);

  const { data: employees, isLoading, error, refetch } = useEmployees(queryOptions);
  const { data: branches = [] } = useBranches(queryOptions);
  const { data: ranks = [] } = useRanks(queryOptions);

  const createMutation = useCreateEmployee(() => {
    setShowForm(false);
    setEditingEmployee(undefined);
    setTimeout(() => refetch(), 100);
  });

  const updateMutation = useUpdateEmployee(() => {
    setShowForm(false);
    setEditingEmployee(undefined);
    setTimeout(() => refetch(), 100);
  });

  const deleteMutation = useDeleteEmployee(() => {
    setTimeout(() => refetch(), 100);
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [branchFilter, setBranchFilter] = useState<string>('all');
  const [rankFilter, setRankFilter] = useState<string>('all');
  const [nationalityFilter, setNationalityFilter] = useState<string>('all');
  const [travelPermitFilter, setTravelPermitFilter] = useState<string>('all');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');
  const [salaryFromFilter, setSalaryFromFilter] = useState<string>('');
  const [salaryToFilter, setSalaryToFilter] = useState<string>('');

  const getBranchName = useCallback((branchId: string) => {
    const branch = branches.find((b: any) => b.id === branchId);
    return branch ? branch.name : '-';
  }, [branches]);

  const uniqueRanks = useMemo(() => {
    if (!employees) return [];
    return Array.from(new Set(employees.map(emp => emp.rank).filter(Boolean)));
  }, [employees]);

  const uniqueNationalities = useMemo(() => {
    if (!employees) return [];
    return Array.from(new Set(employees.map(emp => emp.nationality).filter(Boolean)));
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    if (!employees) return [];
    return employees
      .filter(emp => {
        const empData = emp as any;
        return empData.employee_status === 'active' || emp.status === 'active';
      })
      .filter(employee => {
        const branchName = getBranchName(employee.branchId);
        const matchesSearch = !searchTerm || (
          employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.arabicName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.rank.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.nationalId.toLowerCase().includes(searchTerm.toLowerCase()) ||
          branchName.toLowerCase().includes(searchTerm.toLowerCase())
        );
        const matchesStatus = statusFilter === 'all' || employee.status === statusFilter;
        const matchesBranch = branchFilter === 'all' || employee.branchId === branchFilter;
        const matchesRank = rankFilter === 'all' || employee.rank === rankFilter;
        const matchesNationality = nationalityFilter === 'all' || employee.nationality === nationalityFilter;
        const matchesTravelPermit = travelPermitFilter === 'all' ||
          (travelPermitFilter === 'yes' && employee.travelPermit) ||
          (travelPermitFilter === 'no' && !employee.travelPermit);
        const hireDate = new Date(employee.hireDate);
        const matchesDateFrom = !dateFromFilter || hireDate >= new Date(dateFromFilter);
        const matchesDateTo = !dateToFilter || hireDate <= new Date(dateToFilter);
        const matchesSalaryFrom = !salaryFromFilter || (userRole === 'admin' && employee.salary >= parseFloat(salaryFromFilter));
        const matchesSalaryTo = !salaryToFilter || (userRole === 'admin' && employee.salary <= parseFloat(salaryToFilter));
        return matchesSearch && matchesStatus && matchesBranch && matchesRank &&
          matchesNationality && matchesTravelPermit && matchesDateFrom && matchesDateTo &&
          matchesSalaryFrom && matchesSalaryTo;
      });
  }, [employees, searchTerm, statusFilter, branchFilter, rankFilter, nationalityFilter,
    travelPermitFilter, dateFromFilter, dateToFilter, salaryFromFilter, salaryToFilter, userRole, getBranchName]);

  const handleExport = () => {
    const exportData = filteredEmployees.map(emp => ({
      Code: emp.code, Name: emp.name, 'Arabic Name': emp.arabicName, Rank: emp.rank,
      'الوظيفة': getArabicRankName(ranks, emp.rank, emp.position),
      'National ID': emp.nationalId, 'Card Expiry': emp.cardExpiryDate, Address: emp.address, 'Birth Date': emp.birthDate,
      'Seaman Book Number': emp.seamanBookNumber, 'Seaman Book Expiry': emp.seamanBookExpiry, Nationality: emp.nationality,
      'Travel Permit': emp.travelPermit ? 'Yes' : 'No', 'Phone Number': emp.phoneNumber,
      ...(userRole === 'admin' && { Salary: emp.salary }), 'Amount (Allowance)': emp.amount,
      'Vessel/Headquarters': getBranchName(emp.branchId), 'Hire Date': emp.hireDate
    }));
    exportToExcel(exportData, `employees_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    const exportData = filteredEmployees.map(emp => ({
      Code: emp.code, Name: emp.name, 'Arabic Name': emp.arabicName, Rank: emp.rank,
      'الوظيفة': getArabicRankName(ranks, emp.rank, emp.position),
      'National ID': emp.nationalId, 'Card Expiry': emp.cardExpiryDate, Address: emp.address, 'Birth Date': emp.birthDate,
      'Seaman Book Number': emp.seamanBookNumber, 'Seaman Book Expiry': emp.seamanBookExpiry, Nationality: emp.nationality,
      'Travel Permit': emp.travelPermit ? 'Yes' : 'No', 'Phone Number': emp.phoneNumber, Amount: emp.amount
    }));
    printAsExcel(exportData, 'Employees');
  };

  const columns: TableColumn[] = [
    { key: 'code', label: 'Code', sortable: true },
    { key: 'name', label: 'Name', sortable: true },
    { key: 'arabicName', label: 'الاسم', sortable: true },
    { key: 'rank', label: 'Rank', sortable: true },
    { key: 'position', label: 'الوظيفة', sortable: false, render: (_value: string, item: Employee) => getArabicRankName(ranks, item.rank, item.position) },
    { key: 'nationalId', label: 'الرقم القومي', sortable: true },
    { key: 'cardExpiryDate', label: 'تاريخ انتهاء البطاقة', sortable: true, render: (value: string) => value ? new Date(value).toLocaleDateString() : '-' },
    { key: 'address', label: 'العنوان', sortable: true },
    { key: 'birthDate', label: 'Date of birth', sortable: true, render: (value: string) => value ? new Date(value).toLocaleDateString() : '-' },
    { key: 'seamanBookNumber', label: "Seaman's book number", sortable: true },
    { key: 'seamanBookExpiry', label: "Expiry date of seaman's book", sortable: true, render: (value: string) => value ? new Date(value).toLocaleDateString() : '-' },
    { key: 'nationality', label: 'Nationality', sortable: true },
    { key: 'travelPermit', label: 'Travel permit', sortable: true, render: (value: boolean) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
        {value ? 'Yes' : 'No'}
      </span>
    ) },
    { key: 'phoneNumber', label: 'Mobile number', sortable: false },
    ...(userRole !== 'hr' ? [{ key: 'salary', label: 'Salary', sortable: true, render: (value: number | null) => `${(value ?? 0).toLocaleString()} EGP` }] : []),
    { key: 'amount', label: 'Amount (Allowance)', sortable: true, render: (value: number | null) => `${(value ?? 0).toLocaleString()} EGP` }
  ];

  const handleEdit = (employee: Employee) => { setEditingEmployee(employee); setShowForm(true); };
  const handleDelete = async (employee: Employee) => {
    if (!window.confirm(`Are you sure you want to delete ${employee.name}?`)) return;
    if (!employee?.id) return;
    try {
      await deleteMutation.execute(employee);
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };
  const handleAdd = () => { setEditingEmployee(undefined); setShowForm(true); };

  const handleFormSubmit = async (data: Partial<Employee>) => {
    try {
      if (editingEmployee?.id) {
        await updateMutation.execute({ id: editingEmployee.id, data: { ...data, documentId: editingEmployee.documentId } });
      } else {
        await createMutation.execute(data);
        await bumpCodePrefixSequence(data.code);
      }
    } catch (error) {
      console.error('Form submission failed:', error);
    }
  };

  const handleFormCancel = () => { setShowForm(false); setEditingEmployee(undefined); };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setBranchFilter('all');
    setRankFilter('all');
    setNationalityFilter('all');
    setTravelPermitFilter('all');
    setDateFromFilter('');
    setDateToFilter('');
    setSalaryFromFilter('');
    setSalaryToFilter('');
  };

  const baseFilters: FilterConfig[] = [
    { key: 'branch', label: 'Vessel/Headquarters', type: 'select', value: branchFilter, onChange: setBranchFilter, options: [
      { value: 'all', label: 'All Vessels/HQ' }, ...branches.map((b: any) => ({ value: b.id, label: b.name }))
    ]},
    { key: 'rank', label: 'Rank', type: 'select', value: rankFilter, onChange: setRankFilter, options: [
      { value: 'all', label: 'All Ranks' }, ...uniqueRanks.map(r => ({ value: r, label: r }))
    ]},
    { key: 'nationality', label: 'Nationality', type: 'select', value: nationalityFilter, onChange: setNationalityFilter, options: [
      { value: 'all', label: 'All Nationalities' }, ...uniqueNationalities.map(n => ({ value: n, label: n }))
    ]},
    { key: 'travelPermit', label: 'Travel Permit', type: 'select', value: travelPermitFilter, onChange: setTravelPermitFilter, options: [
      { value: 'all', label: 'All' }, { value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }
    ]},
    { key: 'dateFrom', label: 'Hire Date From', type: 'date', value: dateFromFilter, onChange: setDateFromFilter },
    { key: 'dateTo', label: 'Hire Date To', type: 'date', value: dateToFilter, onChange: setDateToFilter },
  ];

  const filterConfigs: FilterConfig[] = userRole === 'admin'
    ? [...baseFilters,
      { key: 'salaryFrom', label: 'Min Salary', type: 'number', value: salaryFromFilter, onChange: setSalaryFromFilter, placeholder: '0', step: '100' },
      { key: 'salaryTo', label: 'Max Salary', type: 'number', value: salaryToFilter, onChange: setSalaryToFilter, placeholder: '999999', step: '100' },
    ]
    : baseFilters;

  if (isLoading) return <TableSkeleton />;
  if (error) return <ErrorState message="Error loading employees. Please try again." onRetry={() => refetch()} entityName="employees" />;
  if (showForm) {
    return (
      <div className="space-y-6">
        <EmployeeForm employee={editingEmployee} onSubmit={handleFormSubmit} onCancel={handleFormCancel} userRole={userRole} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Employee Management" description="Manage your employees, their branches, and HR documents" />
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search employees..."
        filters={filterConfigs}
        onClearFilters={handleClearFilters}
        totalCount={employees?.length || 0}
        filteredCount={filteredEmployees.length}
        entityName="Employees"
        onAdd={handleAdd}
        addLabel="Add Employee"
        onExport={handleExport}
        onPrint={handlePrint}
      />
      <div className="card">
        <Table columns={columns} data={filteredEmployees} onEdit={handleEdit} onDelete={handleDelete} title="Employee Directory" showAddButton={false} searchable={false} />
      </div>
    </div>
  );
};

export default Employees;
